interface DailyItem {
    name: string;
    days: { [key: number]: number };
    previousMonthTotal: number;
    currentPastMonthTotal?: number;
}

interface DealerData {
    name: string;
    models: string[];
    items: DailyItem[];
}

type DayRange = { start: number; end: number };
type InitializeDaysFn = () => { [key: number]: number };

function getOrCreateDealer(localDealers: DealerData[], dealerName: string): DealerData {
    let dealer = localDealers.find(
        d => d.name.toUpperCase() === dealerName.toUpperCase()
    );
    if (!dealer) {
        // console.warn("Dealer no encontrado en localDealers, se creará:", dealerName);
        dealer = { name: dealerName, models: [], items: [] };
        localDealers.push(dealer);
    }
    return dealer;
}

function getOrCreateItem(dealer: DealerData, modelName: string, initializeDays: InitializeDaysFn): DailyItem {
    let item = dealer.items.find(
        i => i.name.toUpperCase() === modelName.toUpperCase()
    );
    if (!item) {
        item = {
            name: modelName,
            days: initializeDays(),
            previousMonthTotal: 0,
            currentPastMonthTotal: 0,
        };
        dealer.items.push(item);
        if (!dealer.models.includes(modelName)) {
            dealer.models.push(modelName);
        }
    }
    return item;
}

type ProcessStrategy = (item: DailyItem, data: any, dayRange: DayRange) => void;

function processDealerData(
    localDealers: DealerData[],
    dealerName: string,
    items: any,
    dayRange: DayRange,
    initializeDays: InitializeDaysFn,
    processStrategy: ProcessStrategy
): void {
    const dealer = getOrCreateDealer(localDealers, dealerName);
    Object.keys(items || {}).forEach(modelName => {
        const modelData = items[modelName];
        const item = getOrCreateItem(dealer, modelName, initializeDays);
        processStrategy(item, modelData, dayRange);
    });
}

export function processCurrentMonthData(
    localDealers: DealerData[],
    dealerName: string,
    items: any,
    dayRange: DayRange,
    initializeDays: InitializeDaysFn
): void {
    processDealerData(localDealers, dealerName, items, dayRange, initializeDays, (item, modelData, range) => {
        for (let d = range.start; d <= range.end; d++) {
            if (Object.prototype.hasOwnProperty.call(modelData, d)) {
                item.days[d] = Number(modelData[d]);
            }
        }
    });
}

export function processPreviousMonthData(
    localDealers: DealerData[],
    dealerName: string,
    items: any,
    dayRange: DayRange,
    initializeDays: InitializeDaysFn
): void {
    processDealerData(localDealers, dealerName, items, dayRange, initializeDays, (item, modelData) => {
        const sumTotals = Object.keys(modelData).reduce(
            (sum, key) => sum + Number(modelData[key] || 0),
            0
        );
        item.previousMonthTotal = sumTotals;
    });
}

export function processCurrentPastMonthData(
    localDealers: DealerData[],
    dealerName: string,
    items: any,
    dayRange: DayRange,
    initializeDays: InitializeDaysFn
): void {
    processDealerData(localDealers, dealerName, items, dayRange, initializeDays, (item, modelData) => {
        const sumTotals = Object.keys(modelData).reduce(
            (sum, key) => sum + Number(modelData[key] || 0),
            0
        );
        item.currentPastMonthTotal = sumTotals;
    });
}
